<template>
  <div class="branch-dte-editor">
    <v-card
      v-for="section in sections"
      :key="section.key"
      outlined
      class="branch-dte-editor__section"
    >
      <button
        type="button"
        class="branch-dte-editor__section-header"
        @click="toggleSection(section.key)"
      >
        <span class="branch-dte-editor__section-title">{{ section.title }}</span>
        <v-icon small>{{ isSectionOpen(section.key) ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
      </button>

      <v-expand-transition>
        <div v-show="isSectionOpen(section.key)">
          <v-card-text>
            <template v-if="section.key === 'appearance'">
              <div class="branch-dte-editor__appearance-mode mb-4">
                <span class="branch-dte-editor__label">Modo de apariencia</span>
                <v-btn-toggle
                  dense
                  mandatory
                  class="branch-dte-editor__mode-toggle"
                  :value="appearanceMode"
                  @change="updateAppearanceMode"
                >
                  <v-btn small value="preset" :disabled="disabled">
                    Predefinidos
                  </v-btn>
                  <v-btn small value="custom" :disabled="disabled">
                    Personalizado
                  </v-btn>
                </v-btn-toggle>
              </div>

              <div class="branch-dte-editor__color-row mb-2">
                <label class="branch-dte-editor__label">Color encabezados</label>
                <div class="branch-dte-editor__color-inputs">
                  <input
                    class="branch-dte-editor__native-color"
                    type="color"
                    :disabled="disabled"
                    :value="resolvedSettings.theme.headerColor"
                    @change="updateColor($event.target.value)"
                  />
                  <v-text-field
                    dense
                    outlined
                    hide-details
                    readonly
                    class="mb-2"
                    :value="resolvedSettings.theme.headerColor"
                  />
                </div>
              </div>

              <div
                v-if="showGradientControls"
                class="branch-dte-editor__color-row mb-2"
              >
                <label class="branch-dte-editor__label">Color degradado</label>
                <div class="branch-dte-editor__color-inputs">
                  <input
                    class="branch-dte-editor__native-color"
                    type="color"
                    :disabled="disabled"
                    :value="resolvedSettings.theme.gradientColor"
                    @change="updateGradientColor($event.target.value)"
                  />
                  <v-text-field
                    dense
                    outlined
                    hide-details
                    readonly
                    class="mb-2"
                    :value="resolvedSettings.theme.gradientColor"
                  />
                </div>
              </div>

              <div class="branch-dte-editor__color-row mb-2">
                <label class="branch-dte-editor__label">Color borde tablas</label>
                <div class="branch-dte-editor__color-inputs">
                  <input
                    class="branch-dte-editor__native-color"
                    type="color"
                    :disabled="disabled"
                    :value="resolvedSettings.theme.tableBorderColor"
                    @change="updateTableBorderColor($event.target.value)"
                  />
                  <v-text-field
                    dense
                    outlined
                    hide-details
                    readonly
                    class="mb-2"
                    :value="resolvedSettings.theme.tableBorderColor"
                  />
                </div>
              </div>

              <div class="branch-dte-editor__switches mb-2">
                <v-switch
                  inset
                  hide-details
                  class="mb-2"
                  :disabled="disabled"
                  label="No mostrar publicidad"
                  :input-value="resolvedSettings.theme.hideAdvertising"
                  @change="updateThemeFlag('hideAdvertising', $event)"
                />
                <v-switch
                  inset
                  hide-details
                  class="mb-2"
                  :disabled="disabled || resolvedSettings.theme.hideAdvertising"
                  label="Mostrar banner en pagina completa"
                  :input-value="resolvedSettings.theme.bannerFullPage"
                  @change="updateThemeFlag('bannerFullPage', $event)"
                />
                <v-switch
                  inset
                  hide-details
                  class="mb-2"
                  :disabled="disabled || resolvedSettings.theme.hideAdvertising"
                  label="Mostrar publicidad en impresion"
                  :input-value="!resolvedSettings.theme.hideAdvertisingOnPrint"
                  @change="updateInverseThemeFlag('hideAdvertisingOnPrint', $event)"
                />
                <v-switch
                  inset
                  hide-details
                  class="mb-2"
                  :disabled="disabled || resolvedSettings.theme.hideAdvertising"
                  label="Mostrar publicidad en Descargar PDF"
                  :input-value="!resolvedSettings.theme.hideAdvertisingOnPdfDownload"
                  @change="updateInverseThemeFlag('hideAdvertisingOnPdfDownload', $event)"
                />
              </div>

              <v-row v-if="showGradientControls" dense>
                <v-col cols="12">
                  <v-text-field
                    dense
                    outlined
                    hide-details
                    class="mb-2"
                    :disabled="disabled"
                    type="number"
                    :min="limits.gradient.min"
                    :max="limits.gradient.max"
                    label="Intensidad degradado (%)"
                    :value="resolvedSettings.theme.gradientStrength"
                    @change="updateGradientStrength($event)"
                  />
                </v-col>
              </v-row>

              <template v-if="appearanceMode === 'preset'">
                <v-row dense>
                  <v-col cols="12" md="4">
                    <v-select
                      dense
                      outlined
                      hide-details
                      class="mb-2"
                      :disabled="disabled"
                      :items="fontPresetItems"
                      item-text="label"
                      item-value="value"
                      label="Titulos"
                      :value="resolvedSettings.theme.titleFontSizePreset"
                      @change="updatePreset('title', $event)"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select
                      dense
                      outlined
                      hide-details
                      class="mb-2"
                      :disabled="disabled"
                      :items="fontPresetItems"
                      item-text="label"
                      item-value="value"
                      label="Texto general"
                      :value="resolvedSettings.theme.textFontSizePreset"
                      @change="updatePreset('text', $event)"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select
                      dense
                      outlined
                      hide-details
                      class="mb-2"
                      :disabled="disabled"
                      :items="fontPresetItems"
                      item-text="label"
                      item-value="value"
                      label="Detalle"
                      :value="resolvedSettings.theme.detailFontSizePreset"
                      @change="updatePreset('detail', $event)"
                    />
                  </v-col>
                </v-row>

                <v-row dense class="mt-2">
                  <v-col cols="12" md="6">
                    <v-select
                      dense
                      outlined
                      hide-details
                      class="mb-2"
                      :disabled="disabled"
                      :items="fontPresetItems"
                      item-text="label"
                      item-value="value"
                      label="Redondeo en tarjetas"
                      :value="resolvedSettings.theme.cardBorderRadiusPreset"
                      @change="updateRadiusPreset('card', $event)"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      dense
                      outlined
                      hide-details
                      class="mb-2"
                      :disabled="disabled"
                      :items="fontPresetItems"
                      item-text="label"
                      item-value="value"
                      label="Redondeado de tablas"
                      :value="resolvedSettings.theme.tableBorderRadiusPreset"
                      @change="updateRadiusPreset('table', $event)"
                    />
                  </v-col>
                </v-row>
              </template>

              <template v-else>
                <v-row dense class="mb-2">
                  <v-col cols="12" class="mb-2">
                    <b>Tamaño de texto</b>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      dense
                      outlined
                      hide-details
                      class="mb-2"
                      :disabled="disabled"
                      type="number"
                      :min="limits.title.min"
                      :max="limits.title.max"
                      label="Titulos (px)"
                      :value="resolvedSettings.theme.titleFontSizePx"
                      @change="updateFontPx('title', $event)"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      dense
                      outlined
                      hide-details
                      class="mb-2"
                      :disabled="disabled"
                      type="number"
                      :min="limits.text.min"
                      :max="limits.text.max"
                      label="Texto general (px)"
                      :value="resolvedSettings.theme.textFontSizePx"
                      @change="updateFontPx('text', $event)"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      dense
                      outlined
                      hide-details
                      class="mb-2"
                      :disabled="disabled"
                      type="number"
                      :min="limits.detail.min"
                      :max="limits.detail.max"
                      label="Detalle (px)"
                      :value="resolvedSettings.theme.detailFontSizePx"
                      @change="updateFontPx('detail', $event)"
                    />
                  </v-col>
                </v-row>

                <v-row dense>
                  <v-col cols="12" class="mb-2">
                    <b>Redondeado de bordes</b>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      dense
                      outlined
                      hide-details
                      class="mb-2"
                      :disabled="disabled"
                      type="number"
                      :min="limits.radius.min"
                      :max="limits.radius.max"
                      label="Borde tarjetas (px)"
                      :value="resolvedSettings.theme.cardBorderRadiusPx"
                      @change="updateRadiusPx('card', $event)"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field
                      dense
                      outlined
                      hide-details
                      class="mb-2"
                      :disabled="disabled"
                      type="number"
                      :min="limits.radius.min"
                      :max="limits.radius.max"
                      label="Borde tablas (px)"
                      :value="resolvedSettings.theme.tableBorderRadiusPx"
                      @change="updateRadiusPx('table', $event)"
                    />
                  </v-col>
                </v-row>
              </template>
            </template>

            <template v-else-if="section.key === 'pagination'">
              <v-row dense>
                <v-col cols="12" class="mb-2">
                  <v-text-field
                    dense
                    outlined
                    hide-details
                    class="mb-2"
                    :disabled="disabled"
                    type="number"
                    :min="limits.paginationLines.min"
                    :max="limits.paginationLines.max"
                    label="Lineas primera pagina"
                    :value="resolvedSettings.pagination.linesFirstPage"
                    @change="updatePaginationNumber('linesFirstPage', $event)"
                  />
                </v-col>
                <v-col cols="12" class="mb-2">
                  <v-text-field
                    dense
                    outlined
                    hide-details
                    class="mb-2"
                    :disabled="disabled"
                    type="number"
                    :min="limits.paginationLines.min"
                    :max="limits.paginationLines.max"
                    label="Lineas paginas detalle"
                    :value="resolvedSettings.pagination.linesDetailPage"
                    @change="updatePaginationNumber('linesDetailPage', $event)"
                  />
                </v-col>
                <v-col cols="12" class="mb-2">
                  <v-text-field
                    dense
                    outlined
                    hide-details
                    class="mb-2"
                    :disabled="disabled"
                    type="number"
                    :min="limits.paginationLines.min"
                    :max="limits.paginationLines.max"
                    label="Lineas ultima pagina"
                    :value="resolvedSettings.pagination.linesLastPage"
                    @change="updatePaginationNumber('linesLastPage', $event)"
                  />
                </v-col>
              </v-row>

              <div class="branch-dte-editor__switches">
                <v-checkbox
                  hide-details
                  dense
                  class="mt-1"
                  :disabled="disabled"
                  label="Show line guide"
                  :input-value="resolvedSettings.pagination.showLineGuide"
                  @change="updatePaginationFlag('showLineGuide', $event)"
                />
                <v-checkbox
                  hide-details
                  dense
                  class="mt-1"
                  :disabled="disabled"
                  label="Ocultar documentos relacionados en preview"
                  :input-value="resolvedSettings.pagination.hideRelatedDocumentsPreview"
                  @change="updatePaginationFlag('hideRelatedDocumentsPreview', $event)"
                />
              </div>
            </template>

            <template v-else-if="section.key === 'labels'">
              <div
                v-for="group in labelGroups"
                :key="group.key"
                class="branch-dte-editor__label-group"
              >
                <div class="branch-dte-editor__label-group-title">
                  {{ group.title }}
                </div>

                <v-row dense>
                  <v-col
                    v-for="item in group.items"
                    :key="item.key"
                    cols="12"
                    md="6"
                  >
                    <v-text-field
                      dense
                      outlined
                      hide-details
                      counter="60"
                      :maxlength="60"
                      :disabled="disabled"
                      class="mb-2"
                      :label="item.label"
                      :value="resolvedSettings.labels[item.key]"
                      @change="updateLabel(item.key, $event)"
                    />
                  </v-col>
                </v-row>
              </div>
            </template>

            <template v-else>
              <div class="branch-dte-editor__switches">
                <v-switch
                  v-for="item in section.items"
                  :key="item.key"
                  inset
                  hide-details
                  class="mb-2"
                  :disabled="disabled"
                  :label="item.label"
                  :input-value="resolvedSettings.visibility[section.key][item.key]"
                  @change="updateVisibility(section.key, item.key, $event)"
                />
              </div>
            </template>
          </v-card-text>
        </div>
      </v-expand-transition>
    </v-card>
  </div>
</template>

<script>
import {
  DTE_TEMPLATE_CARD_RADIUS_PRESET_MAP,
  DTE_TEMPLATE_DETAIL_FONT_SIZE_MAP,
  DTE_TEMPLATE_FONT_SIZE_PRESETS,
  DTE_TEMPLATE_SIZE_LIMITS,
  DTE_TEMPLATE_TABLE_RADIUS_PRESET_MAP,
  DTE_TEMPLATE_TEXT_FONT_SIZE_MAP,
  DTE_TEMPLATE_TITLE_FONT_SIZE_MAP,
  safeResolveDteTemplateSettings,
  updateTemplateFontPreset,
  updateTemplateFontPx,
  updateTemplateRadiusPreset,
  updateTemplateRadiusPx,
} from "@/helpers/dteTemplateSettings";

export default {
  name: "BranchDteTemplateEditorPanel",
  props: {
    settings: {
      type: Object,
      default: () => ({}),
    },
    layoutKey: {
      type: String,
      default: "estandar",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      appearanceModeByLayout: {},
      openSections: {
        appearance: true,
        pagination: false,
        issuer: false,
        receiver: false,
        document: false,
        detail: false,
        totals: false,
        labels: false,
      },
    };
  },
  computed: {
    resolvedSettings() {
      return safeResolveDteTemplateSettings(this.settings, this.layoutKey);
    },
    fontPresetItems() {
      return DTE_TEMPLATE_FONT_SIZE_PRESETS;
    },
    appearanceMode() {
      return (
        this.appearanceModeByLayout[this.layoutKey] ||
        this.inferAppearanceMode()
      );
    },
    showGradientControls() {
      return this.layoutKey === "completo" || this.layoutKey === "compacto";
    },
    limits() {
      return DTE_TEMPLATE_SIZE_LIMITS;
    },
    sections() {
      return [
        { key: "appearance", title: "Apariencia" },
        { key: "pagination", title: "Paginacion" },
        { key: "issuer", title: "Emisor", items: this.issuerControls },
        { key: "receiver", title: "Receptor", items: this.receiverControls },
        { key: "document", title: "Documento", items: this.documentControls },
        { key: "detail", title: "Detalle", items: this.detailControls },
        { key: "totals", title: "Totales", items: this.totalControls },
        { key: "labels", title: "Etiquetas" },
      ];
    },
    issuerControls() {
      return [
        { key: "address", label: "Mostrar direccion" },
        { key: "businessLine", label: "Mostrar giro" },
        { key: "phone", label: "Mostrar telefono" },
        { key: "email", label: "Mostrar correo" },
      ];
    },
    receiverControls() {
      return [
        { key: "customerCode", label: "Mostrar codigo cliente" },
        { key: "sellerCode", label: "Mostrar codigo vendedor" },
        { key: "sellerName", label: "Mostrar nombre vendedor" },
        { key: "contact", label: "Mostrar contacto" },
        { key: "entity", label: "Mostrar entidad" },
        { key: "currency", label: "Mostrar moneda" },
        { key: "zone", label: "Mostrar zona" },
        { key: "paymentStatus", label: "Mostrar estado de la factura" },
        { key: "economicActivity", label: "Mostrar actividad economica" },
      ];
    },
    documentControls() {
      return [
        { key: "dueDate", label: "Mostrar fecha vencimiento" },
      ];
    },
    detailControls() {
      return [
        { key: "itemType", label: "Mostrar tipo item" },
        { key: "unit", label: "Mostrar unidad" },
        { key: "code", label: "Mostrar codigo" },
        { key: "alternateCode", label: "Mostrar codigo alterno" },
        { key: "discount", label: "Mostrar descuento" },
        { key: "nonSubjectSales", label: "Mostrar ventas no sujetas" },
        { key: "exemptSales", label: "Mostrar ventas exentas" },
        { key: "nonTaxableSales", label: "Mostrar no gravadas" },
      ];
    },
    totalControls() {
      return [
        { key: "discounts", label: "Mostrar total descuentos" },
        { key: "retention", label: "Mostrar retencion" },
        { key: "perception", label: "Mostrar percepcion" },
        { key: "nonTaxable", label: "Mostrar no gravadas" },
        { key: "nonSubject", label: "Mostrar no sujetas" },
        { key: "exempt", label: "Mostrar exentas" },
        { key: "fovial", label: "Mostrar fovial" },
        { key: "cotrans", label: "Mostrar cotrans" },
      ];
    },
    labelControls() {
      return {
        issuer: [
          { key: "phone", label: "Telefono" },
          { key: "email", label: "Correo" },
        ],
        receiver: [
          { key: "customerCode", label: "Codigo cliente" },
          { key: "sellerCode", label: "Codigo vendedor" },
          { key: "sellerName", label: "Nombre vendedor" },
          { key: "contact", label: "Contacto" },
          { key: "entity", label: "Entidad" },
          { key: "currency", label: "Moneda" },
          { key: "zone", label: "Zona" },
          { key: "paymentStatus", label: "Estado de la factura" },
          { key: "economicActivity", label: "Actividad economica" },
        ],
        document: [
          { key: "dueDate", label: "Fecha vencimiento" },
        ],
        detail: [
          { key: "itemNumber", label: "No. Item" },
          { key: "quantity", label: "Cantidad" },
          { key: "description", label: "Descripcion" },
          { key: "mainAmount", label: "Monto principal detalle" },
          { key: "itemType", label: "Tipo item" },
          { key: "unitPrice", label: "Precio unitario" },
          { key: "discount", label: "Descuento" },
          { key: "unit", label: "Unidad" },
          { key: "code", label: "Codigo" },
          { key: "alternateCode", label: "Codigo alterno" },
          { key: "nonSubjectSales", label: "Ventas no sujetas" },
          { key: "exemptSales", label: "Ventas exentas" },
          { key: "nonTaxableSales", label: "No gravadas" },
        ],
        totals: [
          { key: "subtotal", label: "Sub total ventas gravadas" },
          { key: "discounts", label: "Total descuentos" },
          { key: "retention", label: "Retencion" },
          { key: "perception", label: "Percepcion" },
          { key: "nonTaxable", label: "No gravadas total" },
          { key: "nonSubject", label: "No sujetas total" },
          { key: "exempt", label: "Exentas total" },
          { key: "fovial", label: "Fovial" },
          { key: "cotrans", label: "Cotrans" },
        ],
      };
    },
    labelGroups() {
      return [
        {
          key: "detail",
          title: "Etiquetas de Cuerpo del documento",
          items: this.labelControls.detail,
        },
        {
          key: "receiver",
          title: "Etiquetas de Receptor",
          items: this.labelControls.receiver,
        },
        {
          key: "issuer",
          title: "Etiquetas de Emisor",
          items: this.labelControls.issuer,
        },
        {
          key: "document",
          title: "Etiquetas de Documento",
          items: this.labelControls.document,
        },
        {
          key: "totals",
          title: "Etiquetas de Totales",
          items: this.labelControls.totals,
        },
      ];
    },
  },
  methods: {
    inferAppearanceMode() {
      const { theme } = this.resolvedSettings;
      const titleMatchesPreset =
        DTE_TEMPLATE_TITLE_FONT_SIZE_MAP[theme.titleFontSizePreset] === theme.titleFontSizePx;
      const textMatchesPreset =
        DTE_TEMPLATE_TEXT_FONT_SIZE_MAP[theme.textFontSizePreset] === theme.textFontSizePx;
      const detailMatchesPreset =
        DTE_TEMPLATE_DETAIL_FONT_SIZE_MAP[theme.detailFontSizePreset] === theme.detailFontSizePx;
      const cardRadiusMatchesPreset =
        DTE_TEMPLATE_CARD_RADIUS_PRESET_MAP[this.layoutKey][theme.cardBorderRadiusPreset] ===
        theme.cardBorderRadiusPx;
      const tableRadiusMatchesPreset =
        DTE_TEMPLATE_TABLE_RADIUS_PRESET_MAP[this.layoutKey][theme.tableBorderRadiusPreset] ===
        theme.tableBorderRadiusPx;

      return titleMatchesPreset &&
        textMatchesPreset &&
        detailMatchesPreset &&
        cardRadiusMatchesPreset &&
        tableRadiusMatchesPreset
        ? "preset"
        : "custom";
    },
    isSectionOpen(sectionKey) {
      return Boolean(this.openSections[sectionKey]);
    },
    toggleSection(sectionKey) {
      this.$set(this.openSections, sectionKey, !this.isSectionOpen(sectionKey));
    },
    emitSettings(settings) {
      this.$emit("input", settings);
    },
    updateAppearanceMode(mode) {
      const nextMode = mode === "custom" ? "custom" : "preset";
      this.$set(this.appearanceModeByLayout, this.layoutKey, nextMode);

      if (nextMode !== "preset") {
        return;
      }

      let nextSettings = this.resolvedSettings;
      nextSettings = updateTemplateFontPreset(
        nextSettings,
        "title",
        nextSettings.theme.titleFontSizePreset
      );
      nextSettings = updateTemplateFontPreset(
        nextSettings,
        "text",
        nextSettings.theme.textFontSizePreset
      );
      nextSettings = updateTemplateFontPreset(
        nextSettings,
        "detail",
        nextSettings.theme.detailFontSizePreset
      );
      nextSettings = updateTemplateRadiusPreset(
        nextSettings,
        "card",
        nextSettings.theme.cardBorderRadiusPreset,
        this.layoutKey
      );
      nextSettings = updateTemplateRadiusPreset(
        nextSettings,
        "table",
        nextSettings.theme.tableBorderRadiusPreset,
        this.layoutKey
      );
      this.emitSettings(nextSettings);
    },
    updateColor(color) {
      const nextSettings = {
        ...this.resolvedSettings,
        theme: {
          ...this.resolvedSettings.theme,
          headerColor: color,
        },
      };
      this.emitSettings(nextSettings);
    },
    updateGradientColor(color) {
      const nextSettings = {
        ...this.resolvedSettings,
        theme: {
          ...this.resolvedSettings.theme,
          gradientColor: color,
        },
      };
      this.emitSettings(nextSettings);
    },
    updateGradientStrength(value) {
      const numericValue = Number(value);
      const clampedValue = Math.min(
        this.limits.gradient.max,
        Math.max(
          this.limits.gradient.min,
          Number.isFinite(numericValue) ? numericValue : this.limits.gradient.min
        )
      );
      const nextSettings = {
        ...this.resolvedSettings,
        theme: {
          ...this.resolvedSettings.theme,
          gradientStrength: Math.round(clampedValue),
        },
      };
      this.emitSettings(nextSettings);
    },
    updateTableBorderColor(color) {
      const nextSettings = {
        ...this.resolvedSettings,
        theme: {
          ...this.resolvedSettings.theme,
          tableBorderColor: color,
        },
      };
      this.emitSettings(nextSettings);
    },
    updateThemeFlag(key, value) {
      const nextSettings = {
        ...this.resolvedSettings,
        theme: {
          ...this.resolvedSettings.theme,
          [key]: Boolean(value),
        },
      };
      this.emitSettings(nextSettings);
    },
    updateInverseThemeFlag(key, value) {
      const nextSettings = {
        ...this.resolvedSettings,
        theme: {
          ...this.resolvedSettings.theme,
          [key]: !value,
        },
      };
      this.emitSettings(nextSettings);
    },
    updatePreset(fontType, preset) {
      this.emitSettings(updateTemplateFontPreset(this.resolvedSettings, fontType, preset));
    },
    updateFontPx(fontType, value) {
      this.emitSettings(updateTemplateFontPx(this.resolvedSettings, fontType, value));
    },
    updateRadiusPreset(radiusType, preset) {
      this.emitSettings(
        updateTemplateRadiusPreset(this.resolvedSettings, radiusType, preset, this.layoutKey)
      );
    },
    updateRadiusPx(radiusType, value) {
      this.emitSettings(
        updateTemplateRadiusPx(this.resolvedSettings, radiusType, value, this.layoutKey)
      );
    },
    updatePaginationNumber(key, value) {
      const numericValue = Number(value);
      const clampedValue = Math.min(
        this.limits.paginationLines.max,
        Math.max(
          this.limits.paginationLines.min,
          Number.isFinite(numericValue)
            ? numericValue
            : this.resolvedSettings.pagination[key]
        )
      );
      const nextSettings = {
        ...this.resolvedSettings,
        pagination: {
          ...this.resolvedSettings.pagination,
          [key]: Math.round(clampedValue),
        },
      };
      this.emitSettings(nextSettings);
    },
    updatePaginationFlag(key, value) {
      const nextSettings = {
        ...this.resolvedSettings,
        pagination: {
          ...this.resolvedSettings.pagination,
          [key]: Boolean(value),
        },
      };
      this.emitSettings(nextSettings);
    },
    updateVisibility(section, key, value) {
      const nextSettings = {
        ...this.resolvedSettings,
        visibility: {
          ...this.resolvedSettings.visibility,
          [section]: {
            ...this.resolvedSettings.visibility[section],
            [key]: Boolean(value),
          },
        },
      };
      this.emitSettings(nextSettings);
    },
    updateLabel(key, value) {
      const nextSettings = {
        ...this.resolvedSettings,
        labels: {
          ...this.resolvedSettings.labels,
          [key]: value,
        },
      };
      this.emitSettings(nextSettings);
    },
  },
};
</script>

<style scoped>
.branch-dte-editor {
  display: grid;
  gap: 16px;
}

.branch-dte-editor__section {
  border-radius: 16px;
  overflow: hidden;
}

.branch-dte-editor__section-header {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 18px;
  background: #ffffff;
  border: none;
  border-bottom: 1px solid #edf1f6;
  cursor: pointer;
  text-align: left;
}

.branch-dte-editor__section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #25384c;
}

.branch-dte-editor__switches {
  display: grid;
}

.branch-dte-editor__color-row {
  display: grid;
  gap: 8px;
}

.branch-dte-editor__appearance-mode {
  display: grid;
  gap: 10px;
}

.branch-dte-editor__mode-toggle {
  align-self: start;
}

.branch-dte-editor__label {
  font-size: 0.88rem;
  font-weight: 600;
  color: #42576c;
}

.branch-dte-editor__color-inputs {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  gap: 12px;
  align-items: center;
}

.branch-dte-editor__native-color {
  width: 72px;
  height: 42px;
  border: none;
  padding: 0;
  background: transparent;
  cursor: pointer;
}

.branch-dte-editor__label-group + .branch-dte-editor__label-group {
  margin-top: 18px;
}

.branch-dte-editor__label-group-title {
  margin-bottom: 10px;
  font-size: 0.83rem;
  font-weight: 700;
  color: #556b80;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
</style>
